import { asText } from '@prismicio/client'
import type { Ref } from 'vue'

type DocSeoData = {
  title?: unknown
  seo_title?: string | null
  seo_description?: string | null
  seo_image?: { url?: string | null; alt?: string | null } | null
  seo_noindex?: boolean | null
}

type DocLike = { data?: DocSeoData } | null | undefined

function clean(s: unknown): string {
  return typeof s === 'string' ? s.trim() : ''
}

export function useDocumentSeo(doc: Ref<DocLike>) {
  const settings = useSettings()

  const siteTitle = computed(() => asText(settings.value?.data?.siteTitle) || '')

  const pageTitle = computed(() => {
    const data = doc.value?.data
    return clean(data?.seo_title) || asText(data?.title) || ''
  })

  const description = computed(() => clean(doc.value?.data?.seo_description))

  const fullTitle = computed(() => {
    const p = pageTitle.value
    const s = siteTitle.value
    if (!p) return s || undefined
    if (!s || p === s) return p
    return `${p} | ${s}`
  })

  const ogImage = computed(() => doc.value?.data?.seo_image?.url || undefined)
  const ogImageAlt = computed(() => doc.value?.data?.seo_image?.alt || undefined)

  const robots = computed(() =>
    doc.value?.data?.seo_noindex ? 'noindex, nofollow' : undefined,
  )

  useSeoMeta({
    title: () => fullTitle.value,
    description: () => description.value || undefined,
    ogTitle: () => pageTitle.value || siteTitle.value || undefined,
    ogDescription: () => description.value || undefined,
    ogImage: () => ogImage.value,
    ogImageAlt: () => ogImageAlt.value,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: () => pageTitle.value || siteTitle.value || undefined,
    twitterDescription: () => description.value || undefined,
    twitterImage: () => ogImage.value,
    robots: () => robots.value,
  })
}
